// Lightweight front-end lead scoring for quote portal engagement.
// Score is stored in sessionStorage per quote so modal actions can increment it.

const SCORE_PREFIX = 'quote_portal_lead_score:';
const FLAG_PREFIX = 'quote_portal_lead_score_flag:';

export const LEAD_SCORE_WEIGHTS = {
  // Base intent: viewed the quote page
  viewed: 10,

  // Time spent buckets (used by Quote Portal Time Spent)
  timeBuckets: {
    '0-14': 0,
    '15-59': 5,
    '60-179': 10,
    '180+': 20,
  },

  // Modal intent signals
  questionAsked: 25,
  updatesRequested: 35,
  extensionRequested: 30,
  shareLinkCopied: 10,
  openedInEmail: 15,

  approvedConverted: 100,
  approvedNotConverted: 75,

  declined: -50,
};

function getSessionStorage() {
  try {
    if (typeof window === 'undefined') return null;
    if (!window.sessionStorage) return null;
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

export function getLeadScore(quoteId) {
  const ss = getSessionStorage();
  if (!ss) return 0;
  const raw = ss.getItem(`${SCORE_PREFIX}${quoteId}`);
  const n = raw == null ? 0 : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function isFlagSet(quoteId, flagKey) {
  const ss = getSessionStorage();
  if (!ss) return false;
  return ss.getItem(`${FLAG_PREFIX}${quoteId}:${flagKey}`) === '1';
}

function setFlag(quoteId, flagKey) {
  const ss = getSessionStorage();
  if (!ss) return;
  ss.setItem(`${FLAG_PREFIX}${quoteId}:${flagKey}`, '1');
}

/**
 * Adds delta to the quote's running lead score once, keyed by flagKey.
 * Returns the updated total score (clamped to >= 0).
 */
export function addLeadScore(quoteId, delta, flagKey) {
  if (!quoteId) return 0;
  if (!flagKey) flagKey = 'unkeyed';

  if (isFlagSet(quoteId, flagKey)) {
    return getLeadScore(quoteId);
  }

  const ss = getSessionStorage();
  if (!ss) return 0;

  const current = getLeadScore(quoteId);
  const next = Math.max(0, current + delta);
  ss.setItem(`${SCORE_PREFIX}${quoteId}`, String(next));
  setFlag(quoteId, flagKey);
  return next;
}

export function getTimeBucket(timeSeconds) {
  const t = Math.max(0, Math.round(timeSeconds));
  if (t < 15) return '0-14';
  if (t < 60) return '15-59';
  if (t < 180) return '60-179';
  return '180+';
}

export function computeTimeBucketAndPoints(timeSeconds) {
  const timeBucket = getTimeBucket(timeSeconds);
  const points = LEAD_SCORE_WEIGHTS.timeBuckets[timeBucket] ?? 0;
  return { timeBucket, timePoints: points };
}

